import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Mom Emergency arc.
 *
 * Triggered when mom rep <= -10 (you've been ignoring her) AND a random
 * world-tick rolls 1-in-N. Mom calls about Dad. Three decisions decide
 * whether you show up. Outcome touches relationships and sanity, not
 * cash — this is the rare arc that doesn't pretend to be funny.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'MOM: "honey. it\'s about your dad. he\'s in the hospital. they think it\'s his heart. can you come."',
    options: [
      { label: '"i\'m on my way."', effect: () => {
        useLife.getState().bumpRelationship('mom', 30);
        useLife.getState().bumpSanity(-2);
        useMarkets.getState().addCash(-3_500);
      } },
      { label: '"work is crazy. i\'ll call tonight."', effect: () => {
        useLife.getState().bumpRelationship('mom', -25);
        useLife.getState().bumpSanity(-8);
      } },
      { label: '"send me his doctor\'s number. i\'ll handle it."', effect: () => {
        useLife.getState().bumpRelationship('mom', -10);
        useLife.getState().bumpSanity(-4);
      } },
    ],
  },
  {
    text: 'MOM: "he\'s stable. they want to keep him for tests. when can you get here."',
    options: [
      { label: 'fly out today. cancel everything.', effect: () => {
        useLife.getState().bumpRelationship('mom', 15);
        useMarkets.getState().addCash(-2_500);
        useLife.getState().bumpSanity(2);
      } },
      { label: '"this weekend. promise."', effect: () => {
        useLife.getState().bumpRelationship('mom', -15);
        useLife.getState().bumpSanity(-6);
      } },
      { label: 'send a $5K care package and a long voice memo', effect: () => {
        useMarkets.getState().addCash(-5_000);
        useLife.getState().bumpRelationship('mom', 6);
      } },
    ],
  },
];

export function startMomEmergencyArc(): void {
  useMeta.getState().recordLore('arc-mom-emergency', 'Mom Emergency');
  if (!usePhone.getState().contacts['mom']) {
    usePhone.setState((s) => ({
      ...s,
      contacts: { ...s.contacts, 'mom': { id: 'mom', displayName: 'Mom' } },
    }));
  }

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `mom-emerg-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `mom-emerg-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'mom',
        fromNpc: 'mom',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'mom: this is important' });
      sounds.notifyAlert();
    }, i === 0 ? 0 : 60_000);
  }

  // No press post. No tribal swing. No cinematic.
  // The point is that this one is just between you and the family.
}
